import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { Wallet, CheckCircle2, XCircle, Search, Edit3, RefreshCw, Trash2, FileSpreadsheet, FileText, FileType, Upload } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { iuranAPI, kasAPI } from "../lib/api";
import { getWIBParts } from "../components/WIBClock";
import { exportToExcel, exportToPDF, exportToWord, importFromExcel } from "../lib/exportUtils";

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);
const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const MONTH_OPTIONS = (() => {
  const arr = [];
  for (const y of [2025, 2026]) for (const b of BULAN_ID) arr.push(`${b} ${y}`);
  return arr;
})();

const currentWIBMonth = () => {
  const p = getWIBParts();
  return `${p.bulan} ${p.tahun}`;
};

const Iuran = () => {
  const { user, theme } = useApp();
  const canEdit = user.role !== "warga";
  const [items, setItems] = useState([]);
  const [saldoKas, setSaldoKas] = useState(0);
  const [q, setQ] = useState("");
  const [bulan, setBulan] = useState(currentWIBMonth());
  const [statusFilter, setStatusFilter] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => iuranAPI.list().then(setItems).catch(() => {});
  const loadKas = () => kasAPI.get().then((k) => setSaldoKas(k.saldoAkhir || 0)).catch(() => {});
  useEffect(() => {
    load(); loadKas();
    // Realtime polling every 25s
    const iv = setInterval(() => { load(); loadKas(); }, 8000);
    const onFocus = () => { load(); loadKas(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  const filtered = useMemo(() => items.filter((i) => {
    const okQ = !q || (i.blok || "").toLowerCase().includes(q.toLowerCase()) || (i.warga || "").toLowerCase().includes(q.toLowerCase());
    const okB = bulan === "all" || i.bulan === bulan;
    const okS = statusFilter === "all" || i.status === statusFilter;
    return okQ && okB && okS;
  }).sort((a, b) => (a.blok || "").localeCompare(b.blok || "", "id", { numeric: true })), [items, q, bulan, statusFilter]);

  const totalLunas = filtered.filter((i) => i.status === "lunas").reduce((s, i) => s + (i.jumlah || 0), 0);
  const totalBelum = filtered.filter((i) => i.status === "belum").reduce((s, i) => s + (i.jumlah || 0), 0);

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ bulan: row.bulan, jumlah: row.jumlah, status: row.status, metode: row.metode || "-", tanggal: row.tanggal && row.tanggal !== "-" ? row.tanggal : "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        bulan: editForm.bulan,
        jumlah: parseInt(editForm.jumlah, 10) || 0,
        status: editForm.status,
        metode: editForm.metode || "-",
        tanggal: editForm.tanggal || "-",
      };
      await iuranAPI.update(editing.id, payload);
      toast.success("Iuran diperbarui");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const quickLunas = async (id) => {
    try {
      const p = getWIBParts();
      const today = `${p.tahun}-${String(BULAN_ID.indexOf(p.bulan) + 1).padStart(2, "0")}-${String(p.tanggal).padStart(2, "0")}`;
      await iuranAPI.update(id, { status: "lunas", tanggal: today, metode: "Transfer" });
      toast.success("Ditandai LUNAS");
      load();
    } catch { toast.error("Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus data iuran ini?")) return;
    try { await iuranAPI.remove(id); toast.success("Dihapus"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Tambahkan data iuran yang belum ada (Jan 2025 - Des 2026)?\n\nCatatan: data iuran yang sudah tersimpan (termasuk yang berstatus LUNAS) TIDAK akan diubah/dihapus.")) return;
    try {
      const res = await iuranAPI.generate();
      toast.success(`Berhasil: ${res.inserted} data ditambahkan, ${res.kept} data lama dipertahankan`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const periodeLabel = () => (bulan === "all" ? "Semua Periode" : bulan);
  const statusLabel = () => (statusFilter === "all" ? "Semua Status" : statusFilter === "lunas" ? "Lunas" : "Belum");

  const buildRows = () => filtered.map((i, idx) => [
    idx + 1,
    i.blok,
    i.bulan,
    i.jumlah || 0,
    i.status === "lunas" ? "LUNAS" : "BELUM",
    i.metode || "-",
    i.tanggal && i.tanggal !== "-" ? i.tanggal : "-",
  ]);

  const exportExcel = () => {
    const rows = buildRows();
    exportToExcel({
      filename: `iuran-warga-${bulan === "all" ? "semua" : bulan.replace(/\s+/g, "-").toLowerCase()}.xlsx`,
      sheetName: `Iuran ${periodeLabel()}`.slice(0, 31),
      headers: ["No", "Blok", "Bulan", "Nominal (Rp)", "Status", "Metode", "Tanggal Bayar"],
      rows,
      summaryRows: [
        ["", "", "TOTAL LUNAS", totalLunas, "", "", ""],
        ["", "", "TOTAL BELUM", totalBelum, "", "", ""],
        ["", "", "JUMLAH DATA", filtered.length, "", "", ""],
        ["", "", "PERIODE", periodeLabel(), "", "", ""],
        ["", "", "FILTER STATUS", statusLabel(), "", "", ""],
      ],
    });
    toast.success("Excel diunduh");
  };

  const exportPDF = () => {
    const rows = buildRows();
    const lunasCount = filtered.filter((i) => i.status === "lunas").length;
    const belumCount = filtered.filter((i) => i.status === "belum").length;
    exportToPDF({
      filename: `iuran-warga-${bulan === "all" ? "semua" : bulan.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      title: `LAPORAN IURAN WARGA (K3/IPL) — ${periodeLabel()}`,
      subtitle: theme?.appTagline || "Rekap Pembayaran Iuran Bulanan per Blok",
      orgName: (theme?.appName || "WARGA RT DM RESIDENCE").toUpperCase(),
      headers: ["No", "Blok", "Bulan", "Nominal (Rp)", "Status", "Metode", "Tanggal Bayar"],
      rows,
      summary: [
        ["Periode", periodeLabel()],
        ["Filter Status", statusLabel()],
        ["Total Lunas (Rp)", formatIDR(totalLunas)],
        ["Total Belum (Rp)", formatIDR(totalBelum)],
        ["Blok Lunas", `${lunasCount}`],
        ["Blok Belum", `${belumCount}`],
      ],
      orientation: "portrait",
      footer: theme?.footerText || "",
      numericCols: [3],
    });
    toast.success("PDF diunduh");
  };

  // ===== (WhatsApp Reminder / Fonnte dihapus dari aplikasi) =====

  return (
    <div>
      <PageHeader title="Iuran Warga" subtitle="Kelola pembayaran iuran K3/IPL bulanan per blok (auto-sync dari Kas RT)" icon={Wallet}
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportPDF} data-testid="iuran-export-pdf-btn"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={exportExcel} data-testid="iuran-export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" onClick={() => {
            const rows = buildRows();
            exportToWord({ filename: `iuran-${bulan === "all" ? "semua" : bulan.replace(/\s+/g, "-").toLowerCase()}.doc`,
              title: `LAPORAN IURAN WARGA — ${periodeLabel()}`,
              headers: ["No", "Blok", "Bulan", "Nominal", "Status", "Metode", "Tanggal Bayar"], rows,
              summary: [["Periode", periodeLabel()], ["Total Lunas", formatIDR(totalLunas)], ["Total Belum", formatIDR(totalBelum)]]
            });
            toast.success("Word diunduh");
          }} data-testid="iuran-export-word-btn"><FileType className="h-4 w-4 mr-1" />Word</Button>
          {canEdit && <>
            <input type="file" accept=".xlsx,.xls,.csv" id="iuran-import" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              try {
                const rows = await importFromExcel(f);
                if (!rows.length) { toast.error("File kosong"); return; }
                if (!window.confirm(`Import ${rows.length} baris iuran? Match by blok+bulan, status akan di-update.`)) return;
                let ok = 0, fail = 0;
                const existing = await iuranAPI.list();
                for (const r of rows) {
                  const blok = String(r.blok || r.Blok || "").trim();
                  const bulanRow = String(r.bulan || r.Bulan || "").trim();
                  if (!blok || !bulanRow) { fail++; continue; }
                  const status = String(r.status || r.Status || "").toLowerCase().trim() || "belum";
                  const match = existing.find((it) => it.blok === blok && it.bulan === bulanRow);
                  if (!match) { fail++; continue; }
                  try { await iuranAPI.update(match.id, { status, tanggal: String(r.tanggal || "-"), metode: String(r.metode || "-") }); ok++; }
                  catch { fail++; }
                }
                toast.success(`Import selesai: ${ok} sukses, ${fail} gagal`);
                load();
              } catch (err) { toast.error(err.message || "File tidak valid"); }
              e.target.value = "";
            }} />
            <Label htmlFor="iuran-import" className="cursor-pointer" data-testid="iuran-import-label">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold h-9"><Upload className="h-3.5 w-3.5" />Import</span>
            </Label>
          </>}
          {(user.role === "admin" || user.role === "super_admin") && <>
            <Button variant="outline" onClick={async () => {
              try { const r = await iuranAPI.syncFromKas(); toast.success(`Sinkronisasi ${r.processed} transaksi kas`); load(); loadKas(); }
              catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
            }} data-testid="iuran-sync-kas-btn"><RefreshCw className="h-4 w-4 mr-1" />Sync dari Kas</Button>
          </>}
          {user.role === "super_admin" && <Button variant="outline" onClick={regenerate} data-testid="iuran-regenerate-btn"><RefreshCw className="h-4 w-4 mr-1" />Generate Ulang</Button>}
        </div>} />

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <Card className="border-emerald-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">TOTAL LUNAS ({bulan === "all" ? "Semua" : bulan})</div><div className="text-2xl font-extrabold text-emerald-700 mt-1">{formatIDR(totalLunas)}</div></CardContent></Card>
        <Card className="border-red-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">BELUM LUNAS</div><div className="text-2xl font-extrabold text-red-600 mt-1">{formatIDR(totalBelum)}</div></CardContent></Card>
        <Card className="border-amber-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">SALDO KAS RT</div><div className="text-2xl font-extrabold text-amber-600 mt-1">{formatIDR(saldoKas)}</div><div className="text-[10px] text-slate-500 mt-1">Dari saldo akhir Kas RT</div></CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari blok..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Select value={bulan} onValueChange={setBulan}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="all">Semua Bulan</SelectItem>
            {MONTH_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="lunas">Lunas</SelectItem>
            <SelectItem value="belum">Belum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-slate-500 mb-2">Menampilkan <b className="text-slate-700">{filtered.length}</b> data iuran</div>

      <Card className="border-slate-100 overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh] scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-emerald-700 text-white text-xs uppercase sticky top-0 z-10"><tr>
              <th className="text-left p-3">Blok</th>
              <th className="text-left p-3">Bulan</th>
              <th className="text-right p-3">Nominal</th>
              <th className="text-center p-3">Status</th>
              <th className="text-left p-3">Metode</th>
              <th className="text-left p-3">Tanggal</th>
              <th className="text-right p-3">Aksi</th>
            </tr></thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} className="border-t border-emerald-50 hover:bg-emerald-50/40">
                  <td className="p-3 font-bold text-emerald-800">{i.blok}</td>
                  <td className="p-3 text-slate-700">{i.bulan}</td>
                  <td className="p-3 text-right font-semibold text-slate-800">{formatIDR(i.jumlah)}</td>
                  <td className="p-3 text-center">{i.status === "lunas" ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" />Lunas</Badge> : <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Belum</Badge>}</td>
                  <td className="p-3 text-slate-600">{i.metode || "-"}</td>
                  <td className="p-3 text-slate-600">{i.tanggal || "-"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canEdit && i.status === "belum" && <Button size="sm" className="brand-btn mr-1" onClick={() => quickLunas(i.id)} data-testid={`iuran-mark-lunas-${i.blok}`}>Lunas</Button>}
                    {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(i)}><Edit3 className="h-4 w-4" /></Button>}
                    {user.role === "super_admin" && <Button size="sm" variant="ghost" onClick={() => remove(i.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Tidak ada data iuran sesuai filter</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Iuran Blok {editing?.blok}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Bulan</Label>
              <Select value={editForm.bulan} onValueChange={(v) => setEditForm({ ...editForm, bulan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{MONTH_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nominal (Rp)</Label><Input type="number" value={editForm.jumlah} onChange={(e) => setEditForm({ ...editForm, jumlah: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="belum">Belum</SelectItem><SelectItem value="lunas">Lunas</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Metode</Label>
                <Select value={editForm.metode} onValueChange={(v) => setEditForm({ ...editForm, metode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="-">-</SelectItem><SelectItem value="Tunai">Tunai</SelectItem><SelectItem value="Transfer">Transfer</SelectItem><SelectItem value="QRIS">QRIS</SelectItem><SelectItem value="E-Wallet">E-Wallet</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Tanggal Bayar</Label><Input type="date" value={editForm.tanggal} onChange={(e) => setEditForm({ ...editForm, tanggal: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button><Button onClick={saveEdit} className="brand-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device status dialog for Fonnte WhatsApp — DIHAPUS */}
    </div>
  );
};
export default Iuran;
